import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { useEffect, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { PasswordConfirmField } from '@/components/sv/password-confirm-field'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { gql } from '@/lib/graphql/gql'
import {
  useIdCompany,
  useMixpanel,
  usePasswordToken,
  useRBAC,
} from '@/lib/hooks'
import { RoleID, isCompanyDeveloper } from '@/lib/rbac'
import {
  PermissionForm,
  permissionSchema,
} from '@/routes/user-management/permission-form'
import { filterCapabilitiesWithRole } from '@/routes/user-management/permission-form/filter-service-capabilities'
import type {
  CompanyInviteMutation,
  CompanyInviteMutationVariables,
  ProjectInviteMutation,
  ProjectInviteMutationVariables,
  ServiceInviteMutation,
  ServiceInviteMutationVariables,
} from '@/routes/user-management/invite-users-sheet/index.types.gen'

const COMPANY_INVITE = gql`
  mutation CompanyInvite(
    $idCompany: String!
    $emails: [String!]!
    $idRole: String!
    $passwordToken: String
  ) {
    companyInvite(
      idCompany: $idCompany
      emails: $emails
      idRole: $idRole
      passwordToken: $passwordToken
    )
  }
`

const SERVICE_INVITE = gql`
  mutation ServiceInvite(
    $idCompany: String!
    $emails: [String!]!
    $capabilities: [CapabilityInput!]!
  ) {
    serviceInvite(
      idCompany: $idCompany
      emails: $emails
      capabilities: $capabilities
    )
  }
`

const PROJECT_INVITE = gql`
  mutation ProjectInvite(
    $idCompany: String!
    $emails: [String!]!
    $projectCapabilities: [ProjectCapabilityInput!]!
  ) {
    projectInvite(
      idCompany: $idCompany
      emails: $emails
      projectCapabilities: $projectCapabilities
    )
  }
`

const schema = permissionSchema
  .safeExtend({
    emails: z
      .string()
      .min(5, { message: 'Add at least 1 email address' })
      .refine(
        (val) =>
          val
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean)
            .every((email) => z.email().safeParse(email).success),
        { message: 'Enter valid email address' }
      )
      .refine(
        (val) =>
          val
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean).length <= 10,
        {
          message: 'Maximum 10 email addresses can be added',
        }
      ),
    password: z.string().optional(),
  })
  .refine((values) => !(values.type === 'company' && !values.password), {
    message: 'Password is required',
    path: ['password'],
  })
  .refine(
    (values) =>
      !(
        values.type === 'service' &&
        !filterCapabilitiesWithRole(values.capabilities).length
      ),
    {
      message: 'Select at least one service',
      path: ['capabilities'],
    }
  )

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const InviteUsersSheet = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const { rolesInCompany } = useRBAC()
  const { track } = useMixpanel()
  const { obtainPasswordToken } = usePasswordToken()
  const [isPending, startTransition] = useTransition()
  const isCompanyDeveloperViewer = isCompanyDeveloper(rolesInCompany)
  const allowedTypes: Array<'project' | 'service'> | undefined =
    isCompanyDeveloperViewer ? ['project', 'service'] : undefined

  const [companyInvite] = useMutation<
    CompanyInviteMutation,
    CompanyInviteMutationVariables
  >(COMPANY_INVITE)
  const [serviceInvite] = useMutation<
    ServiceInviteMutation,
    ServiceInviteMutationVariables
  >(SERVICE_INVITE)
  const [projectInvite] = useMutation<
    ProjectInviteMutation,
    ProjectInviteMutationVariables
  >(PROJECT_INVITE)

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      emails: '',
      password: '',
      type: isCompanyDeveloperViewer ? 'service' : 'company',
      idRole: isCompanyDeveloperViewer ? undefined : RoleID.COMPANY_ADMIN,
      serviceType: 'All',
      capabilities: [],
      projectCapabilities: [],
    },
  })

  const { handleSubmit, reset, trigger, watch } = form
  const isCompanyInvite =
    watch('type') === 'company' && !isCompanyDeveloperViewer

  useEffect(() => {
    if (isOpen) {
      reset({
        emails: '',
        password: '',
        type: isCompanyDeveloperViewer ? 'service' : 'company',
        idRole: isCompanyDeveloperViewer ? undefined : RoleID.COMPANY_ADMIN,
        serviceType: 'All',
        capabilities: [],
        projectCapabilities: [],
      })
    }
  }, [isCompanyDeveloperViewer, isOpen, reset])

  const onSubmit = async (values: z.infer<typeof schema>) => {
    const isValid = await trigger()
    if (isPending || !isValid) {
      return
    }

    if (isCompanyDeveloperViewer && values.type === 'company') {
      toast(
        <ToasterContent variant="error">
          Company developers can only invite project-level or service-level
          users
        </ToasterContent>,
        { position: 'top-right' }
      )
      return
    }

    startTransition(async () => {
      try {
        const emails = values.emails
          .split(',')
          .map((e) => e.trim())
          .filter(Boolean)
        if (values.type === 'company' && !isCompanyDeveloperViewer) {
          const result = await obtainPasswordToken(values.password ?? '')
          if (result.error) {
            form.setError('password', { message: result.error })
            return
          }
          await companyInvite({
            variables: {
              idCompany,
              emails,
              idRole: values.idRole as string,
              passwordToken: result.passwordToken,
            },
          })
        }
        if (values.type === 'service') {
          await serviceInvite({
            variables: {
              idCompany,
              emails,
              capabilities: filterCapabilitiesWithRole(values.capabilities),
            },
          })
        }
        if (values.type === 'project') {
          const projectCapabilities =
            values.projectCapabilities
              ?.filter((capability) => capability.idRole)
              .map((capability) => ({
                idProject: capability.idProject,
                idRole: capability.idRole,
              })) ?? []
          await projectInvite({
            variables: {
              idCompany,
              emails,
              projectCapabilities,
            },
          })
        }
        toast(
          <ToasterContent variant="success">
            {emails.length > 1
              ? 'Invitation emails have been sent.'
              : 'Invitation email has been sent.'}
          </ToasterContent>,
          { position: 'top-right' }
        )
        const accesLevel =
          values.type === 'company'
            ? (values.idRole ?? 'company').replace(/-/g, ' ')
            : values.type
        track('User invited', { accesLevel })
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to send invite
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Invite users</SheetTitle>
        </SheetHeader>
        <div className="px-4 overflow-y-auto">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="emails"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email(s)</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        placeholder="Enter an email address"
                        aria-label="Email addresses"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Add up to 10 email addresses separated by commas.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <PermissionForm allowedTypes={allowedTypes} />
              {isCompanyInvite ? (
                <PasswordConfirmField
                  description="Company-level invitations require your Sevalla password."
                  isDisabled={isPending}
                />
              ) : null}
              <Button type="submit" hidden />
            </form>
          </Form>
        </div>
        <SheetFooter className="sticky">
          <Button
            type="submit"
            disabled={isPending}
            onClick={handleSubmit(onSubmit)}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Send invitation
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
